import type { ICartItemDetail } from '@/common/interfaces/cart.interface';

export interface IStoreReviewProductColorSwatch {
  hex: string;
  colorName: string;
}

export interface IStoreReviewProductPriceGroup {
  key: string;
  apparelName: string;
  apparelStyleCode: string;
  representativeItem: ICartItemDetail;
  items: ICartItemDetail[];
  colorSwatches: IStoreReviewProductColorSwatch[];
}

export const getStoreReviewProductGroupKey = (item: ICartItemDetail, unitPrice: number): string => {
  const styleCode = item.apparelStyleCode?.trim();
  const productKey = styleCode || `${item.apparelBrand ?? ''}-${item.apparelName ?? ''}`.trim();
  const normalizedPrice = unitPrice.toFixed(2);

  return `${productKey}:${normalizedPrice}`;
};

const getDefaultStoreReviewItemUnitPrice = (item: ICartItemDetail): number =>
  item.apparelPrice ?? item.price ?? 0;

const buildColorSwatches = (items: ICartItemDetail[]): IStoreReviewProductColorSwatch[] => {
  const seen = new Set<string>();
  const swatches: IStoreReviewProductColorSwatch[] = [];

  items.forEach((item) => {
    const hex = item.apparelHex?.trim();
    if (!hex) {
      return;
    }

    const swatchKey = `${hex}-${item.apparelColor ?? ''}`;
    if (seen.has(swatchKey)) {
      return;
    }

    seen.add(swatchKey);
    swatches.push({
      hex,
      colorName: item.apparelColor ?? '',
    });
  });

  return swatches;
};

export const groupStoreReviewItemsByProduct = (
  items: ICartItemDetail[],
  getItemUnitPrice: (item: ICartItemDetail) => number = getDefaultStoreReviewItemUnitPrice
): IStoreReviewProductPriceGroup[] => {
  const groups = new Map<string, IStoreReviewProductPriceGroup>();

  items.forEach((item) => {
    const unitPrice = getItemUnitPrice(item);
    const key = getStoreReviewProductGroupKey(item, unitPrice);
    const existing = groups.get(key);

    if (existing) {
      existing.items.push(item);
      return;
    }

    groups.set(key, {
      key,
      apparelName: item.apparelName ?? '',
      apparelStyleCode: item.apparelStyleCode ?? '',
      representativeItem: item,
      items: [item],
      colorSwatches: [],
    });
  });

  return Array.from(groups.values()).map((group) => ({
    ...group,
    colorSwatches: buildColorSwatches(group.items),
  }));
};
